import React, { useState, useEffect } from 'react';
import { Calendar, Star, Heart, ArrowRight } from 'lucide-react';

// Maintainable data structure — update annually or as needed
const AWARENESS_EVENTS = [
  { month: 1, name: "National Mentoring Month", type: "awareness", color: "blue" },
  { month: 1, day: 22, name: "Bell Let's Talk Day", type: "observance", color: "purple" },
  { month: 2, name: "National Children's Dental Health Month", type: "health", color: "teal" },
  { month: 2, day: 5, name: "National Counseling Awareness Week begins", type: "awareness", color: "blue" },
  { month: 3, name: "Brain Injury Awareness Month", type: "awareness", color: "purple" },
  { month: 3, name: "National Social Work Month", type: "celebration", color: "green" },
  { month: 3, day: 1, name: "Self-Injury Awareness Day", type: "observance", color: "orange" },
  { month: 3, day: 30, name: "World Bipolar Day", type: "observance", color: "purple" },
  { month: 4, name: "Stress Awareness Month", type: "awareness", color: "blue" },
  { month: 4, name: "Alcohol Awareness Month", type: "awareness", color: "orange" },
  { month: 4, name: "National Minority Health Month", type: "awareness", color: "teal" },
  { month: 4, day: 7, name: "World Health Day", type: "observance", color: "green" },
  { month: 5, name: "Mental Health Awareness Month", type: "awareness", color: "green" },
  { month: 5, name: "National Military Appreciation Month", type: "celebration", color: "blue" },
  { month: 5, day: 5, name: "National Children's Mental Health Awareness Day", type: "observance", color: "green" },
  { month: 6, name: "PTSD Awareness Month", type: "awareness", color: "purple" },
  { month: 6, name: "Men's Health Month", type: "health", color: "blue" },
  { month: 6, day: 27, name: "PTSD Awareness Day", type: "observance", color: "purple" },
  { month: 7, name: "Minority Mental Health Awareness Month", type: "awareness", color: "teal" },
  { month: 7, day: 24, name: "International Self-Care Day", type: "celebration", color: "green" },
  { month: 8, name: "National Wellness Month", type: "health", color: "green" },
  { month: 8, day: 8, name: "National Psychologist Day", type: "celebration", color: "blue" },
  { month: 9, name: "National Suicide Prevention Month", type: "awareness", color: "orange" },
  { month: 9, name: "National Recovery Month", type: "awareness", color: "purple" },
  { month: 9, day: 10, name: "World Suicide Prevention Day", type: "observance", color: "orange" },
  { month: 10, name: "National Depression & Mental Health Screening Month", type: "awareness", color: "green" },
  { month: 10, name: "ADHD Awareness Month", type: "awareness", color: "purple" },
  { month: 10, day: 10, name: "World Mental Health Day", type: "observance", color: "green" },
  { month: 11, name: "National Family Caregivers Month", type: "awareness", color: "blue" },
  { month: 11, name: "National Veterans & Military Families Month", type: "celebration", color: "blue" },
  { month: 11, day: 11, name: "Veterans Day — Honoring Mental Health in Service", type: "celebration", color: "blue" },
  { month: 12, day: 3, name: "International Day of Persons with Disabilities", type: "observance", color: "teal" },
  { month: 12, name: "National Impaired Driving Prevention Month", type: "awareness", color: "orange" }
];

const COLOR_MAP = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   dot: 'bg-blue-500' },
  green:  { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-700',  dot: 'bg-green-500' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500' },
  teal:   { bg: 'bg-teal-50',   border: 'border-teal-200',   text: 'text-teal-700',   dot: 'bg-teal-500' }
};

const TYPE_LABELS = {
  awareness: 'Awareness',
  observance: 'Observance',
  celebration: 'Celebration',
  health: 'Health'
};

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const MentalHealthAwareness = () => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const currentMonth = now.getMonth() + 1;
  const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;

  // Current month events + next month events for upcoming
  const currentEvents = AWARENESS_EVENTS.filter(e => e.month === currentMonth);
  const upcomingEvents = AWARENESS_EVENTS.filter(e => e.month === nextMonth).slice(0, 3);

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white" data-testid="awareness-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
            <Heart className="w-4 h-4" />
            Live Awareness Updates
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Mental Health Awareness & Observances
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Recognizing the dates, observances, and celebrations that matter in behavioral health.
          </p>
        </div>

        {/* Current Month Highlight */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-900">
              {MONTH_NAMES[currentMonth]} {now.getFullYear()} — This Month
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentEvents.map((event) => {
              const c = COLOR_MAP[event.color] || COLOR_MAP.blue;
              return (
                <div
                  key={event.name}
                  className={`${c.bg} ${c.border} border rounded-xl p-4 flex items-start gap-3 transition-shadow hover:shadow-md`}
                  data-testid={`awareness-event-${event.name}`}
                >
                  <div className={`w-3 h-3 ${c.dot} rounded-full mt-1.5 flex-shrink-0`}></div>
                  <div>
                    <p className={`font-semibold ${c.text} text-sm`}>{event.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {event.day ? `${MONTH_NAMES[event.month]} ${event.day}` : `All ${MONTH_NAMES[event.month]}`}
                      {' — '}
                      <span className="font-medium">{TYPE_LABELS[event.type]}</span>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming */}
        {upcomingEvents.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <ArrowRight className="w-5 h-5 text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-700">
                Coming in {MONTH_NAMES[nextMonth]}
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {upcomingEvents.map((event) => {
                const c = COLOR_MAP[event.color] || COLOR_MAP.blue;
                return (
                  <div
                    key={event.name}
                    className={`${c.bg} ${c.border} border rounded-lg px-4 py-2.5 flex items-center gap-2`}
                  >
                    <Star className={`w-4 h-4 ${c.text}`} />
                    <span className={`text-sm font-medium ${c.text}`}>{event.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MentalHealthAwareness;
